import React, { useEffect, useMemo, useState } from "react";
import { currency, numberOrZero } from "../utils/formatters";
import { openHtmlWindow } from "../utils/printDoc";

// ─────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────
const DS = {
  blue:      "#1D6FE8",
  blueSoft:  "#E8F0FD",
  teal:      "#0E8C6A",
  tealSoft:  "#E5F5F0",
  amber:     "#D08B20",
  amberSoft: "#FDF4E3",
  red:       "#C9352A",
  redSoft:   "#FBE9E8",
  green:     "#16A34A",
  greenSoft: "#F0FDF4",
  muted:     "#64748B",
  border:    "#E2E8F2",
  surface:   "#F8FAFC",
  white:     "#FFFFFF",
  text:      "#0F172A",
  r8:  "8px", r12: "12px", r16: "16px", r20: "20px",
  shadowSm: "0 1px 4px rgba(15,23,42,0.07)",
  shadowMd: "0 4px 16px rgba(15,23,42,0.10)",
};

// ─────────────────────────────────────────────
// UTILITIES — all logic identical to original
// ─────────────────────────────────────────────
function hasUsableNumber(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function varianceColor(value) {
  const n = numberOrZero(value);
  if (n > 0)  return DS.green;
  if (n < 0)  return DS.red;
  return DS.text;
}

function varianceStyle(value) {
  return { color: varianceColor(value), fontWeight: 700 };
}

function clampPercentage(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, value);
}

// Time-based programme progress:
// (today − start date) / (revised end − start date), clamped 0–100.
// Falls back to other completion dates when no revised date is set.
// A Closed project keeps its stored progress value.
function timeProgress(src) {
  if (!src) return 0;
  if (src.status === "Closed") return clampPercentage(numberOrZero(src.progress));

  const start = src.startDate;
  const end =
    src.revisedEndDate ||
    src.revisedCompletionDate ||
    src.estimatedEndDate ||
    src.completionDate;
  if (!start || !end) return 0;

  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;

  const total = e.getTime() - s.getTime();
  if (total <= 0) return 0;

  const elapsed = Date.now() - s.getTime();
  const pct = (elapsed / total) * 100;
  return Math.max(0, Math.min(100, pct));
}

function toMonthView(month) {
  if (!month) return { key: "", label: "", shortLabel: "", year: "" };
  if (typeof month === "string") {
    const date = new Date(`${month}-01`);
    return {
      key: month,
      label: date.toLocaleDateString(undefined, { month: "long",  year: "numeric" }),
      shortLabel: date.toLocaleDateString(undefined, { month: "short" }),
      year: month.slice(0, 4),
      monthNo: month.slice(5, 7),
    };
  }
  return {
    key:        month.key        || "",
    label:      month.label      || month.key || "",
    shortLabel: month.shortLabel || month.label || month.key || "",
    year:       month.year       || String(month.key || "").slice(0, 4),
    monthNo:    month.monthNo    || String(month.key || "").slice(5, 7),
  };
}

function toMonthKeyFromDate(value) {
  if (!value) return "";
  const t = String(value).trim();
  if (/^\d{4}-\d{2}$/.test(t))     return t;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 7);
  const d = new Date(t);
  if (Number.isNaN(d.getTime()))    return "";
  return d.toISOString().slice(0, 7);
}

function getActualCpaValue(row) {
  if (!row) return 0;
  const candidates = [
    row.cpa, row.cpaThisCertificate, row.cpaCertifiedValue,
    row.cpaCumulative, row.actualCpa, row.cpaAmount, row.cpaThisMonth,
    row.monthlyCpa, row.certifiedCpa, row.certifiedCPA, row.cpaCertified,
    row.cpaCertifiedThisPeriod, row.cpaThisPeriod, row.thisCertificateCpa,
    row.thisPeriodCpa, row.contractPriceAdjustment, row.priceAdjustment, row.escalation,
  ];
  return numberOrZero(candidates.find(hasUsableNumber));
}

function getActualExtraWorkValue(row) {
  if (!row) return 0;

  // ── 1. Try flat scalar fields first ──────────────────────────────────────
  const candidates = [
    row.extraWork, row.extraWorks, row.vo, row.vos, row.variation, row.variations,
    row.extraWorkThisCertificate, row.extraWorksThisCertificate,
    row.voThisCertificate, row.vosThisCertificate,
    row.approvedVoThisCertificate, row.approvedVosThisCertificate,
    row.extraWorkAmount, row.voAmount, row.variationAmount,
  ];
  const flat = candidates.find(hasUsableNumber);
  if (flat !== undefined && flat !== null) return numberOrZero(flat);

  // ── 2. Fall back to summing line-item arrays (extraWorkItems / variationItems / voItems)
  //       Each entry may carry amountThisCertificate, amount, certifiedAmount, or value.
  const items =
    (Array.isArray(row.extraWorkItems)  ? row.extraWorkItems  : null) ||
    (Array.isArray(row.variationItems)  ? row.variationItems  : null) ||
    (Array.isArray(row.voItems)         ? row.voItems         : null) ||
    (Array.isArray(row.variations)      ? row.variations      : null) ||
    [];

  if (items.length > 0) {
    return items.reduce((sum, entry) => {
      const val = numberOrZero(
        entry.amountThisCertificate ??
        entry.amount               ??
        entry.certifiedAmount      ??
        entry.value                ?? 0
      );
      return sum + val;
    }, 0);
  }

  return 0;
}

function getActualCertificateTotals(row) {
  if (!row) return { actualTotalExCpa: 0, actualTotalIncCpa: 0, actualCpa: 0, actualExtraWork: 0, actualScheduled: 0 };

  const actualCpa       = hasUsableNumber(row.cpaInitialB) ? numberOrZero(row.cpaInitialB) : getActualCpaValue(row);
  const actualExtraWork = getActualExtraWorkValue(row);

  const paidMovement = [
    row.paidAmountMovement, row.paidMovement, row.paidAmountDifference,
    row.paidAmountThisCertificate, row.amountPaidThisCertificate,
    row.actualTotalIncCpa, row.totalIncCpaActual, row.totalIncCpa,
  ].find(hasUsableNumber);

  const calculatedPaidMovement =
    hasUsableNumber(row.currentPaidAmount) || hasUsableNumber(row.previousPaidAmount)
      ? numberOrZero(row.currentPaidAmount) - numberOrZero(row.previousPaidAmount)
      : null;

  const actualTotalIncCpa = hasUsableNumber(row.scheduledWorksPlusCpaInitialC)
    ? numberOrZero(row.scheduledWorksPlusCpaInitialC)
    : hasUsableNumber(paidMovement)
      ? numberOrZero(paidMovement)
      : calculatedPaidMovement !== null
        ? calculatedPaidMovement
        : numberOrZero(row.scheduledWorks) + actualExtraWork + actualCpa;

  const actualTotalExCpa = hasUsableNumber(row.scheduledWorksA)
    ? numberOrZero(row.scheduledWorksA)
    : Math.max(0, actualTotalIncCpa - actualCpa);

  const actualScheduled = Math.max(0, actualTotalExCpa - actualExtraWork);

  return { actualTotalExCpa, actualTotalIncCpa, actualCpa, actualExtraWork, actualScheduled };
}

function getActualMonthlyTotal(row) {
  return getActualCertificateTotals(row).actualTotalIncCpa;
}

// ─────────────────────────────────────────────
// COLUMN DEFINITIONS
// ─────────────────────────────────────────────
const CASHFLOW_TABLE_COLUMNS = [
  { key: "month",                          label: "Month",                          alwaysVisible: true },
  { key: "forecastScheduled",              label: "Forecast Scheduled" },
  { key: "actualScheduled",               label: "Scheduled Works (Actual)" },
  { key: "scheduledWorksA",               label: "Scheduled Works (A)" },
  { key: "varianceScheduled",             label: "Variance Scheduled",             isVariance: true },
  { key: "forecastCpa",                   label: "Forecast CPA" },
  { key: "actualCpa",                     label: "Actual CPA" },
  { key: "cpaInitialB",                   label: "CPA Initial (B)" },
  { key: "scheduledWorksPlusCpaInitialC", label: "Scheduled Works + CPA Initial (C)" },
  { key: "varianceCpa",                   label: "Variance CPA",                   isVariance: true },
  { key: "forecastExtraWork",             label: "Forecast Extra Work / VOs" },
  { key: "forecastTotalExCpa",            label: "Forecast Ex CPA" },
  { key: "forecastTotalIncCpa",           label: "Forecast Inc CPA" },
  { key: "actualExtraWork",              label: "Actual Extra Work / VOs" },
  { key: "actualTotalExCpa",             label: "Total Ex CPA (Actual)" },
  { key: "varianceTotalExCpa",           label: "Variance Total Ex CPA",           isVariance: true },
  { key: "varianceExtraWork",            label: "Variance Extra Work / VOs",       isVariance: true },
  { key: "forecastTotal",                label: "Forecast Total" },
  { key: "actualTotal",                  label: "Total Inc CPA (Actual)" },
  { key: "varianceTotalIncCpa",          label: "Variance Total Inc CPA",          isVariance: true },
  { key: "variance",                     label: "Variance Total",                  isVariance: true },
  { key: "cumulativeForecastCpa",        label: "Cumulative Forecast CPA" },
  { key: "cumulativeActualCpa",          label: "Cumulative Actual CPA" },
  { key: "cumulativeVariance",           label: "Cumulative Variance",             isVariance: true },
];

const DEFAULT_VISIBLE = CASHFLOW_TABLE_COLUMNS.reduce((acc, c) => { acc[c.key] = true; return acc; }, {});
const DEFAULT_ORDER   = CASHFLOW_TABLE_COLUMNS.map(c => c.key);

// ─────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", size = "md", disabled, type = "button" }) {
  const base = {
    border: "none", borderRadius: DS.r8, fontWeight: 600, cursor: "pointer",
    fontFamily: "inherit", transition: "all 0.15s", lineHeight: 1,
    padding: size === "sm" ? "6px 12px" : "9px 14px",
    fontSize: size === "sm" ? 11 : 12,
    opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto",
    display: "inline-flex", alignItems: "center", gap: 5,
  };
  const variants = {
    primary:   { background: DS.blue,    color: "#fff" },
    secondary: { background: DS.surface, color: DS.text, border: `1px solid ${DS.border}` },
    success:   { background: DS.teal,    color: "#fff" },
    danger:    { background: DS.red,     color: "#fff" },
    ghost:     { background: "transparent", color: DS.blue, border: `1px solid ${DS.border}` },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...(variants[variant] || variants.primary) }}>
      {children}
    </button>
  );
}

function SectionCard({ title, subtitle, children, action, accent, collapsed, onToggle }) {
  return (
    <div style={{
      background: DS.white, border: `1px solid ${DS.border}`,
      borderRadius: DS.r20, boxShadow: DS.shadowSm, overflow: "hidden", marginBottom: 14,
      ...(accent ? { borderTop: `3px solid ${accent}` } : {}),
    }}>
      <div style={{
        padding: "13px 20px", borderBottom: collapsed ? "none" : `1px solid ${DS.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
        cursor: onToggle ? "pointer" : "default",
      }} onClick={onToggle}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DS.text }}>{title}</h3>
          {subtitle && <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>{subtitle}</p>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {action && <div onClick={e => e.stopPropagation()}>{action}</div>}
          {onToggle && (
            <span style={{ fontSize: 11, color: DS.muted, userSelect: "none" }}>
              {collapsed ? "▼ Show" : "▲ Hide"}
            </span>
          )}
        </div>
      </div>
      {!collapsed && <div style={{ padding: "16px 20px" }}>{children}</div>}
    </div>
  );
}

function KpiCard({ label, value, sub, accent, valueStyle: vs }) {
  return (
    <div style={{
      background: DS.white, border: `1px solid ${DS.border}`,
      borderRadius: DS.r12, padding: "12px 14px",
      boxShadow: DS.shadowSm, borderTop: `3px solid ${accent || DS.blue}`,
    }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.08em", color: DS.muted }}>{label}</p>
      <p style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 700,
        letterSpacing: "-0.02em", color: DS.text, ...(vs || {}) }}>{value}</p>
      {sub && <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>{sub}</p>}
    </div>
  );
}

// Shared input style
const inp = {
  width: "100%", boxSizing: "border-box",
  border: `1px solid ${DS.border}`, borderRadius: DS.r8,
  padding: "7px 10px", fontSize: 12, color: DS.text,
  background: DS.white, fontFamily: "inherit",
};
const lbl = {
  display: "block", marginBottom: 4, fontSize: 10, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.07em", color: DS.muted,
};

// ─────────────────────────────────────────────
// ADD ADDITIONAL MONTH MODAL
// Replaces the original window.prompt() calls
// ─────────────────────────────────────────────
function AddMonthModal({ monthKey, monthLabel, onConfirm, onCancel }) {
  const [scheduled, setScheduled] = useState("0");
  const [cpa,       setCpa]       = useState("0");
  const [extraWork, setExtraWork] = useState("0");

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(11,22,40,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, zIndex: 1000,
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(480px, 96vw)", background: DS.white, borderRadius: DS.r20,
        boxShadow: "0 20px 60px rgba(15,23,42,0.2)", overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 22px", borderBottom: `1px solid ${DS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Add Additional Month</h3>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>{monthLabel}</p>
          </div>
          <Btn variant="secondary" size="sm" onClick={onCancel}>✕</Btn>
        </div>
        <div style={{ padding: "20px 22px", display: "grid", gap: 14 }}>
          {[
            ["Forecast Scheduled Works", scheduled, setScheduled],
            ["Forecast CPA",             cpa,       setCpa],
            ["Forecast Extra Work / VOs",extraWork, setExtraWork],
          ].map(([label, val, set]) => (
            <div key={label}>
              <label style={lbl}>{label}</label>
              <input style={inp} type="number" value={val} onChange={e => set(e.target.value)} />
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
            <Btn onClick={() => onConfirm({ scheduled, cpa, extraWork })}>
              Add Month
            </Btn>
            <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// NEW CASHFLOW REVISION MODAL
// Parent record: each revision is a full cashflow the contractor
// submitted, tagged with the month it reflects (required).
// ─────────────────────────────────────────────
function NewRevisionModal({ defaultMonth, existingMonths = [], onConfirm, onCancel }) {
  const [month, setMonth]   = useState(defaultMonth || new Date().toISOString().slice(0, 7));
  const [name,  setName]    = useState("");
  const [seed,  setSeed]    = useState(true);
  const [error, setError]   = useState("");

  const monthTaken = existingMonths.includes(month);

  function handleConfirm() {
    if (!/^\d{4}-\d{2}$/.test(month)) {
      setError("Select the month this cashflow reflects.");
      return;
    }
    onConfirm({ month, name: name.trim(), seedFromActive: seed });
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(11,22,40,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, zIndex: 1000,
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(480px, 96vw)", background: DS.white, borderRadius: DS.r20,
        boxShadow: "0 20px 60px rgba(15,23,42,0.2)", overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 22px", borderBottom: `1px solid ${DS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>New Cashflow Revision</h3>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>
              Bundle a full contractor cashflow under the month it reflects
            </p>
          </div>
          <Btn variant="secondary" size="sm" onClick={onCancel}>✕</Btn>
        </div>
        <div style={{ padding: "20px 22px", display: "grid", gap: 14 }}>
          <div>
            <label style={lbl}>Cashflow Month <span style={{ color: DS.red }}>*</span></label>
            <input
              style={inp}
              type="month"
              value={month}
              onChange={e => { setMonth(e.target.value); setError(""); }}
            />
            {monthTaken && (
              <p style={{ margin: "5px 0 0", fontSize: 11, color: DS.amber, fontWeight: 600 }}>
                A revision already exists for this month — this will be an additional one.
              </p>
            )}
          </div>
          <div>
            <label style={lbl}>Revision Name / Reference</label>
            <input
              style={inp}
              type="text"
              placeholder="e.g. Rev A, Resubmission, Contractor v2"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <label style={{
            display: "flex", alignItems: "center", gap: 9, fontSize: 12,
            color: DS.text, cursor: "pointer", fontWeight: 600,
          }}>
            <input type="checkbox" checked={seed}
              onChange={e => setSeed(e.target.checked)}
              style={{ accentColor: DS.blue, width: 15, height: 15 }} />
            Start from the currently selected revision's figures
          </label>
          {error && (
            <p style={{ margin: 0, fontSize: 12, color: DS.red, fontWeight: 600 }}>{error}</p>
          )}
          <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
            <Btn onClick={handleConfirm}>Create Revision</Btn>
            <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// BAR CHART — upgraded version
// ─────────────────────────────────────────────
function CashflowBarChart({ rows }) {
  const maxValue = Math.max(1, ...rows.flatMap(r => [r.forecastTotal, r.actualTotal]));
  return (
    <div>
      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 12 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: DS.blue, display: "inline-block" }} />
          Forecast
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: DS.teal, display: "inline-block" }} />
          Actual
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: DS.red, display: "inline-block" }} />
          Variance (over)
        </span>
      </div>

      {/* Bars */}
      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(${rows.length}, minmax(40px, 1fr))`,
        gap: 6, alignItems: "end", height: 160,
      }}>
        {rows.map(row => {
          const fPct = (row.forecastTotal / maxValue) * 140;
          const aPct = (row.actualTotal  / maxValue) * 140;
          const isOver = row.actualTotal > row.forecastTotal;
          return (
            <div key={row.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 140 }}>
                {/* Forecast bar */}
                <div title={`Forecast: ${currency(row.forecastTotal)}`} style={{
                  width: 10, height: `${Math.max(2, fPct)}px`,
                  background: DS.blue, borderRadius: "3px 3px 0 0",
                  transition: "height 0.3s",
                }} />
                {/* Actual bar */}
                <div title={`Actual: ${currency(row.actualTotal)}`} style={{
                  width: 10, height: `${Math.max(2, aPct)}px`,
                  background: isOver ? DS.red : DS.teal,
                  borderRadius: "3px 3px 0 0",
                  transition: "height 0.3s",
                }} />
              </div>
              <span style={{ fontSize: 9, color: DS.muted, textAlign: "center", lineHeight: 1.2 }}>
                {row.shortLabel}<br />{row.year}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
export default function CashflowSection({
  currentProject,
  monthRange,
  activeForecastBlocks,
  activeActualBlocks,
  updateBlockValue,
  additionalCashflowMonths = [],
  handleAddAdditionalCashflowMonth,
  handleDeleteAdditionalCashflowMonth,
  // ── Cashflow revisions (parent records) ──
  cashflowRevisions = [],
  selectedCashflowRevisionId = "",
  onSelectRevision,
  onCreateRevision,
  onDeleteRevision,
}) {
  const [asAtDate,                setAsAtDate]                = useState(() => new Date().toISOString().slice(0, 10));
  const [showSummary,             setShowSummary]             = useState(true);
  const [showForecast,            setShowForecast]            = useState(true);
  const [showActual,              setShowActual]              = useState(true);
  const [expandedFYears,          setExpandedFYears]          = useState({});
  const [selectedForecastMonthKey,setSelectedForecastMonthKey]= useState("");
  const [selectedActualMonthKey,  setSelectedActualMonthKey]  = useState("");
  const [selectedAdditionalMonth, setSelectedAdditionalMonth] = useState("");
  const [showColumnOptions,       setShowColumnOptions]       = useState(false);
  const [addMonthModal,           setAddMonthModal]           = useState(false); // replaces window.prompt
  const [newRevisionModal,        setNewRevisionModal]        = useState(false);

  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const s = window.localStorage.getItem("cashflowTableVisibleColumns");
      return s ? { ...DEFAULT_VISIBLE, ...JSON.parse(s), month: true } : DEFAULT_VISIBLE;
    } catch { return DEFAULT_VISIBLE; }
  });

  const [colOrder, setColOrder] = useState(() => {
    try {
      const s   = window.localStorage.getItem("cashflowTableColumnOrder");
      const raw = s ? JSON.parse(s) : DEFAULT_ORDER;
      const valid   = CASHFLOW_TABLE_COLUMNS.map(c => c.key);
      const ordered = raw.filter(k => valid.includes(k));
      const missing = valid.filter(k => !ordered.includes(k));
      return ["month", ...ordered.filter(k => k !== "month"), ...missing.filter(k => k !== "month")];
    } catch { return DEFAULT_ORDER; }
  });

  // Persist column prefs
  useEffect(() => { window.localStorage.setItem("cashflowTableVisibleColumns", JSON.stringify(visibleCols)); }, [visibleCols]);
  useEffect(() => { window.localStorage.setItem("cashflowTableColumnOrder",    JSON.stringify(colOrder));    }, [colOrder]);

  // ── Month range normalization ─────────────────
  const normalizedMonthRange = useMemo(() =>
    monthRange.map(toMonthView).filter(m => m.key),
    [monthRange]
  );

  const normalizedAdditionalMonths = useMemo(() =>
    (additionalCashflowMonths || [])
      .map(m => toMonthView(typeof m === "string" ? m : m.key || m.id))
      .filter(m => m.key),
    [additionalCashflowMonths]
  );

  const additionalMonthOptions = useMemo(() => {
    const endKey =
      toMonthKeyFromDate(currentProject?.revisedEndDate) ||
      toMonthKeyFromDate(currentProject?.revisedCompletionDate) ||
      toMonthKeyFromDate(currentProject?.completionDate) ||
      normalizedMonthRange[normalizedMonthRange.length - 1]?.key || "";
    if (!endKey) return [];
    const options = [];
    const start   = new Date(`${endKey}-01`);
    for (let i = 1; i <= 12; i++) {
      const d = new Date(start);
      d.setMonth(d.getMonth() + i);
      options.push(toMonthView(d.toISOString().slice(0, 7)));
    }
    return options;
  }, [currentProject, normalizedMonthRange]);

  const extendedMonthRange = useMemo(() => {
    const combined = [...normalizedMonthRange, ...normalizedAdditionalMonths];
    const byKey = combined.reduce((acc, m) => { if (m?.key) acc[m.key] = m; return acc; }, {});
    return Object.values(byKey).sort((a, b) => String(a.key).localeCompare(String(b.key)));
  }, [normalizedMonthRange, normalizedAdditionalMonths]);

  // Sync selected month keys to valid values
  useEffect(() => {
    if (!selectedForecastMonthKey && extendedMonthRange.length)
      setSelectedForecastMonthKey(extendedMonthRange[0].key);
    else if (selectedForecastMonthKey && extendedMonthRange.length &&
      !extendedMonthRange.some(m => m.key === selectedForecastMonthKey))
      setSelectedForecastMonthKey(extendedMonthRange[0].key);
  }, [extendedMonthRange, selectedForecastMonthKey]);

  useEffect(() => {
    if (!selectedActualMonthKey && extendedMonthRange.length)
      setSelectedActualMonthKey(extendedMonthRange[0].key);
    else if (selectedActualMonthKey && extendedMonthRange.length &&
      !extendedMonthRange.some(m => m.key === selectedActualMonthKey))
      setSelectedActualMonthKey(extendedMonthRange[0].key);
  }, [extendedMonthRange, selectedActualMonthKey]);

  useEffect(() => {
    if (!selectedAdditionalMonth && additionalMonthOptions.length)
      setSelectedAdditionalMonth(additionalMonthOptions[0].key);
    else if (selectedAdditionalMonth && additionalMonthOptions.length &&
      !additionalMonthOptions.some(m => m.key === selectedAdditionalMonth))
      setSelectedAdditionalMonth(additionalMonthOptions[0].key);
  }, [additionalMonthOptions, selectedAdditionalMonth]);

  // ── Column helpers ────────────────────────────
  const orderedColumns = useMemo(() => {
    const map = CASHFLOW_TABLE_COLUMNS.reduce((acc, c) => { acc[c.key] = c; return acc; }, {});
    const ordered  = colOrder.map(k => map[k]).filter(Boolean);
    const missing  = CASHFLOW_TABLE_COLUMNS.filter(c => !ordered.some(o => o.key === c.key));
    return [...ordered, ...missing];
  }, [colOrder]);

  const visibleColumns = useMemo(() =>
    orderedColumns.filter(c => c.alwaysVisible || visibleCols[c.key]),
    [orderedColumns, visibleCols]
  );

  function toggleColumn(key) {
    const col = CASHFLOW_TABLE_COLUMNS.find(c => c.key === key);
    if (col?.alwaysVisible) return;
    setVisibleCols(prev => ({ ...prev, [key]: !prev[key], month: true }));
  }

  function setColumnPosition(key, posVal) {
    if (key === "month") return;
    const pos = Math.max(2, Math.min(numberOrZero(posVal), DEFAULT_ORDER.length));
    setColOrder(prev => {
      const cleaned = ["month", ...prev.filter(k => k !== "month"),
        ...DEFAULT_ORDER.filter(k => !prev.includes(k) && k !== "month")];
      const ci = cleaned.indexOf(key);
      if (ci <= 0) return cleaned;
      const next = [...cleaned];
      const [moved] = next.splice(ci, 1);
      next.splice(pos - 1, 0, moved);
      return next;
    });
  }

  function selectAllCols()  { setVisibleCols(DEFAULT_VISIBLE); }
  function resetColOrder()  { setColOrder(DEFAULT_ORDER); }
  function showCoreCols()   {
    setVisibleCols({
      ...DEFAULT_VISIBLE,
      forecastCpa: false, actualCpa: false, varianceCpa: false,
      forecastExtraWork: false, actualExtraWork: false, varianceExtraWork: false,
      cumulativeForecastCpa: false, cumulativeActualCpa: false, cumulativeVariance: false,
      month: true,
    });
  }

  function getCellValue(row, key) {
    if (key === "month") return row.label;
    return currency(numberOrZero(row[key]));
  }

  function getCellStyle(row, col) {
    if (!col.isVariance) return undefined;
    return varianceStyle(numberOrZero(row[col.key]));
  }

  function toggleFY(fy) {
    setExpandedFYears(prev => ({ ...prev, [fy]: !(prev[fy] ?? true) }));
  }

  // ── Cashflow revisions ───────────────────────
  const sortedRevisions = useMemo(() =>
    [...(cashflowRevisions || [])].sort((a, b) =>
      String(b.month || b.key || "").localeCompare(String(a.month || a.key || ""))
    ),
    [cashflowRevisions]
  );

  const selectedRevision = useMemo(() =>
    sortedRevisions.find(r => r.id === selectedCashflowRevisionId) || null,
    [sortedRevisions, selectedCashflowRevisionId]
  );

  function revisionLabel(rev) {
    if (!rev) return "";
    const monthPart = toMonthView(rev.month || rev.key || "").label || (rev.month || rev.key || "");
    const namePart  = String(rev.name || "").trim();
    return namePart ? `${monthPart} — ${namePart}` : monthPart;
  }

  // ── Grouped data ─────────────────────────────
  const grouped = useMemo(() => {
    const groups = {};
    extendedMonthRange.forEach(month => {
      const date = new Date(`${month.key}-01`);
      const fyStart = date.getMonth() >= 6 ? date.getFullYear() : date.getFullYear() - 1;
      const fy      = `${fyStart}/${fyStart + 1}`;
      if (!groups[fy]) groups[fy] = { runningVariance: 0, runningForecastCpa: 0, runningActualCpa: 0, rows: [] };

      const forecastRow  = activeForecastBlocks[month.key] || { scheduledWorks: "", cpa: "", extraWork: "" };
      const actualRow    = activeActualBlocks[month.key]   || { scheduledWorks: "", cpa: "", extraWork: "" };

      const forecastScheduled = numberOrZero(forecastRow.scheduledWorks);
      const forecastCpa       = numberOrZero(forecastRow.cpa);
      const forecastExtraWork = numberOrZero(forecastRow.extraWork);

      const actualTotals  = getActualCertificateTotals(actualRow);
      const actualScheduled = actualTotals.actualScheduled;
      const actualCpa       = actualTotals.actualCpa;
      const actualExtraWork = actualTotals.actualExtraWork;

      const scheduledWorksA = hasUsableNumber(actualRow.scheduledWorksA)
        ? numberOrZero(actualRow.scheduledWorksA) : actualTotals.actualTotalExCpa;
      const cpaInitialB     = hasUsableNumber(actualRow.cpaInitialB)
        ? numberOrZero(actualRow.cpaInitialB) : actualTotals.actualCpa;
      const scheduledWorksPlusCpaInitialC = hasUsableNumber(actualRow.scheduledWorksPlusCpaInitialC)
        ? numberOrZero(actualRow.scheduledWorksPlusCpaInitialC) : scheduledWorksA + cpaInitialB;

      const forecastTotalExCpa  = forecastScheduled + forecastExtraWork;
      const forecastTotalIncCpa = forecastScheduled + forecastExtraWork + forecastCpa;
      const forecastTotal       = forecastTotalIncCpa;
      const actualTotalExCpa    = actualTotals.actualTotalExCpa;
      const actualTotal         = actualTotals.actualTotalIncCpa;

      const varianceScheduled   = actualScheduled - forecastScheduled;
      const varianceCpa         = actualCpa - forecastCpa;
      const varianceExtraWork   = actualExtraWork - forecastExtraWork;
      const varianceTotalExCpa  = actualTotalExCpa - forecastTotalExCpa;
      const varianceTotalIncCpa = actualTotal - forecastTotalIncCpa;
      const variance            = varianceTotalIncCpa;

      groups[fy].runningVariance   += variance;
      groups[fy].runningForecastCpa += forecastCpa;
      groups[fy].runningActualCpa   += actualCpa;

      groups[fy].rows.push({
        ...month,
        forecastScheduled, forecastCpa, forecastExtraWork,
        forecastTotalExCpa, forecastTotalIncCpa, forecastTotal,
        actualScheduled, scheduledWorksA, actualCpa, cpaInitialB,
        scheduledWorksPlusCpaInitialC, actualExtraWork, actualTotalExCpa, actualTotal,
        varianceScheduled, varianceCpa, varianceExtraWork,
        varianceTotalExCpa, varianceTotalIncCpa, variance,
        cumulativeVariance:    groups[fy].runningVariance,
        cumulativeForecastCpa: groups[fy].runningForecastCpa,
        cumulativeActualCpa:   groups[fy].runningActualCpa,
      });
    });
    return Object.fromEntries(
      Object.entries(groups).map(([fy, val]) => [fy, val.rows])
    );
  }, [extendedMonthRange, activeForecastBlocks, activeActualBlocks]);

  // ── KPI aggregations ─────────────────────────
  const asAt = useMemo(() => new Date(asAtDate), [asAtDate]);

  const rowsToDate = useMemo(() =>
    extendedMonthRange
      .map(month => {
        const fr = activeForecastBlocks[month.key] || {};
        const ar = activeActualBlocks[month.key]   || {};
        const at = getActualCertificateTotals(ar);
        return {
          ...month,
          forecastScheduled: numberOrZero(fr.scheduledWorks),
          forecastCpa:       numberOrZero(fr.cpa),
          forecastExtraWork: numberOrZero(fr.extraWork),
          forecastTotal: numberOrZero(fr.scheduledWorks) + numberOrZero(fr.cpa) + numberOrZero(fr.extraWork),
          actualCpa:      at.actualCpa,
          actualExtraWork:at.actualExtraWork,
          actualTotal:    at.actualTotalIncCpa,
        };
      })
      .filter(r => new Date(`${r.key}-01`) <= asAt),
    [extendedMonthRange, activeForecastBlocks, activeActualBlocks, asAt]
  );

  const forecastToDate   = useMemo(() => rowsToDate.reduce((s, r) => s + r.forecastTotal, 0), [rowsToDate]);
  const actualToDate     = useMemo(() => rowsToDate.reduce((s, r) => s + r.actualTotal,   0), [rowsToDate]);
  const forecastCpaToDate = useMemo(() => rowsToDate.reduce((s, r) => s + r.forecastCpa,   0), [rowsToDate]);
  const actualCpaToDate   = useMemo(() => rowsToDate.reduce((s, r) => s + r.actualCpa,     0), [rowsToDate]);

  const overallForecast = useMemo(() =>
    extendedMonthRange.reduce((s, m) => {
      const r = activeForecastBlocks[m.key] || {};
      return s + numberOrZero(r.scheduledWorks) + numberOrZero(r.cpa) + numberOrZero(r.extraWork);
    }, 0), [extendedMonthRange, activeForecastBlocks]
  );

  const overallActual = useMemo(() =>
    extendedMonthRange.reduce((s, m) => {
      const r = activeActualBlocks[m.key] || {};
      return s + getActualMonthlyTotal({ ...r, cpa: getActualCpaValue(r), extraWork: getActualExtraWorkValue(r) });
    }, 0), [extendedMonthRange, activeActualBlocks]
  );

  const progressAgainstBudget = useMemo(() => {
    const denom = numberOrZero(currentProject?.grandTotal);
    return denom ? Math.round((actualToDate / denom) * 100) : 0;
  }, [actualToDate, currentProject?.grandTotal]);

  const programmeProgress = useMemo(
    () => Math.round(timeProgress(currentProject)),
    [
      currentProject?.startDate,
      currentProject?.revisedEndDate,
      currentProject?.revisedCompletionDate,
      currentProject?.estimatedEndDate,
      currentProject?.completionDate,
      currentProject?.status,
      currentProject?.progress,
    ]
  );

  const progressDelta = useMemo(() =>
    progressAgainstBudget - programmeProgress, [progressAgainstBudget, programmeProgress]);

  // ── PDF export helpers (unchanged logic) ─────
  function exportFYPdf(financialYear, rows) {
    const totals = {
      forecastExclCpa:  rows.reduce((s, r) => s + r.forecastScheduled, 0),
      forecastCpa:      rows.reduce((s, r) => s + r.forecastCpa, 0),
      forecastExtraWork:rows.reduce((s, r) => s + numberOrZero(r.forecastExtraWork), 0),
      forecastInclCpa:  rows.reduce((s, r) => s + r.forecastTotal, 0),
      actualCpa:        rows.reduce((s, r) => s + r.actualCpa, 0),
      actualExtraWork:  rows.reduce((s, r) => s + getActualExtraWorkValue(r), 0),
      actual:           rows.reduce((s, r) => s + r.actualTotal, 0),
    };
    const variance = totals.actual - totals.forecastInclCpa;
    const bodyRows = rows.map(r => `
      <tr>
        <td>${r.label}</td>
        <td>${currency(r.forecastScheduled)}</td><td>${currency(r.actualScheduled)}</td>
        <td>${currency(r.varianceScheduled)}</td><td>${currency(r.forecastCpa)}</td>
        <td>${currency(r.actualCpa)}</td><td>${currency(r.varianceCpa)}</td>
        <td>${currency(r.forecastExtraWork)}</td><td>${currency(r.actualExtraWork)}</td>
        <td>${currency(r.actualTotalExCpa)}</td><td>${currency(r.varianceTotalExCpa)}</td>
        <td>${currency(r.varianceExtraWork)}</td><td>${currency(r.forecastTotal)}</td>
        <td>${currency(r.actualTotal)}</td><td>${currency(r.varianceTotalIncCpa)}</td>
        <td>${currency(r.variance)}</td><td>${currency(r.cumulativeForecastCpa)}</td>
        <td>${currency(r.cumulativeActualCpa)}</td><td>${currency(r.cumulativeVariance)}</td>
      </tr>`).join("");
    openHtmlWindow(`<html><head><title>${financialYear} FY Summary</title>
      <style>body{font-family:Arial;padding:24px}table{width:100%;border-collapse:collapse;margin-top:16px}
      th,td{border:1px solid #ccc;padding:6px;font-size:11px}th{background:#f3f4f6}</style></head>
      <body><h2>${financialYear} Financial Year Summary</h2><p><strong>As At:</strong> ${asAtDate}</p>
      <table><tr><th>Forecast excl CPA</th><th>Forecast CPA</th><th>Forecast Extra Work</th>
      <th>Forecast incl CPA</th><th>Actual CPA</th><th>Actual Extra Work</th>
      <th>Actual</th><th>Variance</th></tr>
      <tr><td>${currency(totals.forecastExclCpa)}</td><td>${currency(totals.forecastCpa)}</td>
      <td>${currency(totals.forecastExtraWork)}</td><td>${currency(totals.forecastInclCpa)}</td>
      <td>${currency(totals.actualCpa)}</td><td>${currency(totals.actualExtraWork)}</td>
      <td>${currency(totals.actual)}</td><td>${currency(variance)}</td></tr></table>
      <table><tr><th>Month</th><th>Forecast Sched.</th><th>Actual Sched.</th><th>Var Sched.</th>
      <th>Fcst CPA</th><th>Act CPA</th><th>Var CPA</th><th>Fcst Extra</th><th>Act Extra</th>
      <th>Act Ex CPA</th><th>Var Ex CPA</th><th>Var Extra</th><th>Fcst Total</th>
      <th>Act Total</th><th>Var Total Inc CPA</th><th>Variance</th>
      <th>Cum Fcst CPA</th><th>Cum Act CPA</th><th>Cum Var</th></tr>${bodyRows}</table></body></html>`,
      { features: "width=1000,height=800", autoPrint: true });
  }

  function exportSummaryPdf() {
    const summaryRows = Object.entries(grouped).map(([fy, rows]) => {
      const actualFiltered = rows.filter(r => new Date(`${r.key}-01`) <= asAt);
      const fExclCpa  = rows.reduce((s, r) => s + r.forecastScheduled, 0);
      const fCpa      = rows.reduce((s, r) => s + r.forecastCpa, 0);
      const fTotal    = rows.reduce((s, r) => s + r.forecastTotal, 0);
      const aCpa      = actualFiltered.reduce((s, r) => s + r.actualCpa, 0);
      const aTotal    = actualFiltered.reduce((s, r) => s + r.actualTotal, 0);
      return `<tr><td>${fy}</td><td>${currency(fExclCpa)}</td><td>${currency(fCpa)}</td>
        <td>${currency(fTotal)}</td><td>${currency(aCpa)}</td>
        <td>${currency(aTotal)}</td><td>${currency(aTotal - fTotal)}</td></tr>`;
    }).join("");
    openHtmlWindow(`<html><head><title>FY Summary</title>
      <style>body{font-family:Arial;padding:24px}table{width:100%;border-collapse:collapse;margin-top:16px}
      th,td{border:1px solid #ccc;padding:6px;font-size:11px}th{background:#f3f4f6}</style></head>
      <body><h2>Financial Year Summary (to Date)</h2><p><strong>As At:</strong> ${asAtDate}</p>
      <table><tr><th>FY</th><th>Fcst excl CPA</th><th>Fcst CPA</th><th>Fcst incl CPA</th>
      <th>Act CPA (to date)</th><th>Actual (to date)</th><th>Variance</th></tr>
      ${summaryRows}</table></body></html>`,
      { features: "width=1000,height=800", autoPrint: true });
  }

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════

  // Overall variance KPI cards
  const overallVariance  = overallActual - overallForecast;
  const varianceToDate   = actualToDate  - forecastToDate;

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif" }}>

      {/* ── ADD MONTH MODAL ─────────────────────── */}
      {addMonthModal && (
        <AddMonthModal
          monthKey={selectedAdditionalMonth}
          monthLabel={additionalMonthOptions.find(m => m.key === selectedAdditionalMonth)?.label || selectedAdditionalMonth}
          onCancel={() => setAddMonthModal(false)}
          onConfirm={({ scheduled, cpa, extraWork }) => {
            if (typeof handleAddAdditionalCashflowMonth === "function")
              handleAddAdditionalCashflowMonth(selectedAdditionalMonth);
            updateBlockValue("forecast", selectedAdditionalMonth, "scheduledWorks", scheduled || "0");
            updateBlockValue("forecast", selectedAdditionalMonth, "cpa",           cpa       || "0");
            updateBlockValue("forecast", selectedAdditionalMonth, "extraWork",      extraWork  || "0");
            setSelectedForecastMonthKey(selectedAdditionalMonth);
            setSelectedActualMonthKey(selectedAdditionalMonth);
            setAddMonthModal(false);
          }}
        />
      )}

      {/* ── NEW REVISION MODAL ──────────────────── */}
      {newRevisionModal && (
        <NewRevisionModal
          defaultMonth={selectedRevision?.month || extendedMonthRange[extendedMonthRange.length - 1]?.key || ""}
          existingMonths={sortedRevisions.map(r => r.month || r.key).filter(Boolean)}
          onCancel={() => setNewRevisionModal(false)}
          onConfirm={(payload) => {
            if (typeof onCreateRevision === "function") onCreateRevision(payload);
            setNewRevisionModal(false);
          }}
        />
      )}

      {/* ── PAGE HEADER ─────────────────────────── */}
      <div style={{
        background: DS.white, border: `1px solid ${DS.border}`, borderRadius: DS.r20,
        boxShadow: DS.shadowSm, padding: "16px 22px", marginBottom: 14,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 12, borderTop: `3px solid ${DS.blue}`,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: DS.text,
            letterSpacing: "-0.02em" }}>
            Budget & Cashflow
          </h2>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: DS.muted }}>
            {currentProject?.id || "No project selected"} · as at{" "}
            <strong>{new Date(asAtDate).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}</strong>
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: DS.muted }}>As At</label>
          <input type="date" value={asAtDate} onChange={e => setAsAtDate(e.target.value)}
            style={{ ...inp, width: "auto" }} />
        </div>
      </div>

      {/* ── CASHFLOW REVISION (PARENT SELECTOR) ─── */}
      <div style={{
        background: DS.white, border: `1px solid ${DS.border}`, borderRadius: DS.r20,
        boxShadow: DS.shadowSm, padding: "14px 22px", marginBottom: 14,
        borderTop: `3px solid ${DS.amber}`,
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-end",
          flexWrap: "wrap", gap: 12,
        }}>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <label style={lbl}>Cashflow Revision</label>
            {sortedRevisions.length > 0 ? (
              <select
                style={inp}
                value={selectedCashflowRevisionId || ""}
                onChange={e => { if (typeof onSelectRevision === "function") onSelectRevision(e.target.value); }}
              >
                {!selectedCashflowRevisionId && <option value="">— Select a revision —</option>}
                {sortedRevisions.map(rev => (
                  <option key={rev.id} value={rev.id}>{revisionLabel(rev)}</option>
                ))}
              </select>
            ) : (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: DS.muted }}>
                No revisions yet. Create one to bundle a contractor cashflow under the month it reflects.
              </p>
            )}
            <p style={{ margin: "6px 0 0", fontSize: 11, color: DS.muted }}>
              Each revision is a complete cashflow your contractor submitted. Selecting a revision
              pulls up its forecast figures across every month and financial year below.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn variant="primary" size="sm" onClick={() => setNewRevisionModal(true)}>
              + New Cashflow Revision
            </Btn>
            {selectedRevision && (
              <Btn variant="danger" size="sm" onClick={() => {
                if (!window.confirm(`Delete cashflow revision "${revisionLabel(selectedRevision)}"? This cannot be undone.`)) return;
                if (typeof onDeleteRevision === "function") onDeleteRevision(selectedRevision.id);
              }}>
                Delete Revision
              </Btn>
            )}
          </div>
        </div>
        {selectedRevision && (
          <div style={{
            marginTop: 12, display: "flex", gap: 16, flexWrap: "wrap",
            paddingTop: 12, borderTop: `1px dashed ${DS.border}`,
          }}>
            <span style={{ fontSize: 11, color: DS.muted }}>
              Reflects month:{" "}
              <strong style={{ color: DS.amber }}>
                {toMonthView(selectedRevision.month || selectedRevision.key || "").label || "—"}
              </strong>
            </span>
            {selectedRevision.name && (
              <span style={{ fontSize: 11, color: DS.muted }}>
                Reference: <strong style={{ color: DS.text }}>{selectedRevision.name}</strong>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── SUMMARY KPI CARDS ───────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 10, marginBottom: 14 }}>
        <KpiCard label="Total Forecast"        value={currency(overallForecast)}      accent={DS.blue} />
        <KpiCard label="Total Actual"          value={currency(overallActual)}        accent={DS.teal} />
        <KpiCard label="Overall Variance"      value={currency(overallVariance)}      accent={overallVariance >= 0 ? DS.teal : DS.red}
          valueStyle={varianceStyle(overallVariance)} />
        <KpiCard label="Forecast to Date"      value={currency(forecastToDate)}       accent={DS.blue} />
        <KpiCard label="Actual to Date"        value={currency(actualToDate)}         accent={DS.teal} />
        <KpiCard label="Variance to Date"      value={currency(varianceToDate)}       accent={varianceToDate >= 0 ? DS.teal : DS.red}
          valueStyle={varianceStyle(varianceToDate)} />
        <KpiCard label="Forecast CPA to Date"  value={currency(forecastCpaToDate)}    accent={DS.blue} />
        <KpiCard label="Actual CPA to Date"    value={currency(actualCpaToDate)}      accent={DS.teal} />
        <KpiCard label="Programme Progress"    value={`${programmeProgress}%`}        accent={DS.blue} />
        <KpiCard label="Spend vs Grand Total"  value={`${progressAgainstBudget}%`}    accent={DS.teal} />
        <KpiCard label="Spend vs Programme"
          value={`${progressDelta > 0 ? "+" : ""}${progressDelta}%`}
          accent={progressDelta >= 0 ? DS.teal : DS.red}
          valueStyle={{ color: progressDelta >= 0 ? DS.teal : DS.red, fontWeight: 700 }}
          sub={progressDelta > 5 ? "Ahead of programme" : progressDelta < -5 ? "Behind programme" : "On track"} />
      </div>

      {/* ── PROJECT PROGRAMME SUMMARY ───────────── */}
      <SectionCard
        title="Project Programme Summary"
        subtitle="Financial year breakdown and key project dates"
        collapsed={!showSummary}
        onToggle={() => setShowSummary(v => !v)}
        action={
          <Btn variant="primary" size="sm" onClick={exportSummaryPdf}>
            ⬇ Export Summary PDF
          </Btn>
        }
      >
        {/* Dates strip */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))",
          gap: 10, marginBottom: 16,
        }}>
          {[
            ["Project Start",     currentProject?.startDate    || "—"],
            ["Estimated End",     currentProject?.completionDate || "—"],
            ["Cashflow End",      extendedMonthRange.length ? extendedMonthRange[extendedMonthRange.length - 1].label : "—"],
          ].map(([label, val]) => (
            <div key={label} style={{
              background: DS.surface, border: `1px solid ${DS.border}`,
              borderRadius: DS.r8, padding: "10px 14px",
            }}>
              <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.07em", color: DS.muted }}>{label}</p>
              <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 600 }}>{val}</p>
            </div>
          ))}
        </div>

        {/* Financial year summary table */}
        <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: DS.text }}>
          Financial Year Summary (to Date)
        </h4>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {["Financial Year","Forecast excl. CPA","Forecast CPA","Forecast incl. CPA",
                  "Actual Ex CPA","Actual CPA","Actual Incl CPA",
                  "Variance (Actual inc CPA - Forecast inc CPA)"].map(h => (
                  <th key={h} style={{ padding: "7px 10px", textAlign: "left",
                    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.07em", color: DS.muted,
                    borderBottom: `2px solid ${DS.border}`, background: DS.surface,
                    whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(grouped).map(([fy, rows]) => {
                const af  = rows.filter(r => new Date(`${r.key}-01`) <= asAt);
                const fExc = rows.reduce((s, r) => s + r.forecastScheduled + numberOrZero(r.forecastExtraWork), 0);
                const fCpa = rows.reduce((s, r) => s + r.forecastCpa, 0);
                const fInc = rows.reduce((s, r) => s + r.forecastTotal, 0);
                const aExc = af.reduce((s, r) => s + numberOrZero(r.actualTotalExCpa), 0);
                const aCpa = af.reduce((s, r) => s + r.actualCpa, 0);
                const aInc = af.reduce((s, r) => s + r.actualTotal, 0);
                const vari = aInc - fInc;
                return (
                  <tr key={fy} style={{ borderBottom: `1px solid ${DS.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = DS.surface}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{fy}</td>
                    <td style={{ padding: "8px 10px" }}>{currency(fExc)}</td>
                    <td style={{ padding: "8px 10px" }}>{currency(fCpa)}</td>
                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{currency(fInc)}</td>
                    <td style={{ padding: "8px 10px" }}>{currency(aExc)}</td>
                    <td style={{ padding: "8px 10px" }}>{currency(aCpa)}</td>
                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{currency(aInc)}</td>
                    <td style={{ padding: "8px 10px", ...varianceStyle(vari) }}>{currency(vari)}</td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr style={{ borderTop: `2px solid ${DS.border}`, background: DS.surface, fontWeight: 700 }}>
                <td style={{ padding: "8px 10px" }}>Total</td>
                {[
                  Object.values(grouped).reduce((s, rows) => s + rows.reduce((rs, r) => rs + r.forecastScheduled + numberOrZero(r.forecastExtraWork), 0), 0),
                  Object.values(grouped).reduce((s, rows) => s + rows.reduce((rs, r) => rs + r.forecastCpa, 0), 0),
                  Object.values(grouped).reduce((s, rows) => s + rows.reduce((rs, r) => rs + r.forecastTotal, 0), 0),
                ].map((v, i) => (
                  <td key={i} style={{ padding: "8px 10px" }}>{currency(v)}</td>
                ))}
                {[
                  Object.values(grouped).reduce((s, rows) => s + rows.filter(r => new Date(`${r.key}-01`) <= asAt).reduce((rs, r) => rs + numberOrZero(r.actualTotalExCpa), 0), 0),
                  Object.values(grouped).reduce((s, rows) => s + rows.filter(r => new Date(`${r.key}-01`) <= asAt).reduce((rs, r) => rs + r.actualCpa, 0), 0),
                  Object.values(grouped).reduce((s, rows) => s + rows.filter(r => new Date(`${r.key}-01`) <= asAt).reduce((rs, r) => rs + r.actualTotal, 0), 0),
                ].map((v, i) => (
                  <td key={`a${i}`} style={{ padding: "8px 10px" }}>{currency(v)}</td>
                ))}
                {(() => {
                  const fInc = Object.values(grouped).reduce((s, rows) => s + rows.reduce((rs, r) => rs + r.forecastTotal, 0), 0);
                  const aAct = Object.values(grouped).reduce((s, rows) => s + rows.filter(r => new Date(`${r.key}-01`) <= asAt).reduce((rs, r) => rs + r.actualTotal, 0), 0);
                  const vari = aAct - fInc;
                  return <td style={{ padding: "8px 10px", ...varianceStyle(vari) }}>{currency(vari)}</td>;
                })()}
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* ── FORECAST & ACTUAL INPUT ─────────────── */}
      <SectionCard
        title="Forecast & Actual Cashflow"
        subtitle={
          selectedRevision
            ? `Editing revision: ${revisionLabel(selectedRevision)}. Actuals are pulled from certified payment certificates.`
            : sortedRevisions.length > 0
              ? "Select a cashflow revision above to edit its forecast. Actuals are pulled from certified payment certificates."
              : "Select a month to update forecast values. Actuals are pulled from certified payment certificates."
        }
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="secondary" size="sm" onClick={() => setShowForecast(v => !v)}>
              {showForecast ? "Hide Forecast" : "Show Forecast"}
            </Btn>
            <Btn variant="secondary" size="sm" onClick={() => setShowActual(v => !v)}>
              {showActual ? "Hide Actual" : "Show Actual"}
            </Btn>
          </div>
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

          {/* Forecast panel */}
          {showForecast && (
            <div style={{
              background: DS.blueSoft, border: `1px solid #BDD3F9`,
              borderRadius: DS.r16, padding: "16px 18px",
            }}>
              <h4 style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: DS.blue }}>
                Forecast Cashflow
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                <div>
                  <label style={lbl}>Forecast Month</label>
                  <select style={inp} value={selectedForecastMonthKey}
                    onChange={e => setSelectedForecastMonthKey(e.target.value)}>
                    {extendedMonthRange.map(m => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Additional Month</label>
                  <select style={inp} value={selectedAdditionalMonth}
                    onChange={e => setSelectedAdditionalMonth(e.target.value)}>
                    {additionalMonthOptions.map(m => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                <Btn size="sm" onClick={() => {
                  if (!selectedAdditionalMonth) return;
                  setAddMonthModal(true);
                }}>
                  + Add Month
                </Btn>
                {normalizedAdditionalMonths.some(m => m.key === selectedForecastMonthKey) && (
                  <Btn size="sm" variant="danger" onClick={() => {
                    if (typeof handleDeleteAdditionalCashflowMonth === "function")
                      handleDeleteAdditionalCashflowMonth(selectedForecastMonthKey);
                  }}>
                    Delete Month
                  </Btn>
                )}
              </div>

              {(() => {
                const month = extendedMonthRange.find(m => m.key === selectedForecastMonthKey) || extendedMonthRange[0];
                if (!month) return null;
                const row  = activeForecastBlocks[month.key] || {};
                const total = numberOrZero(row.scheduledWorks) + numberOrZero(row.cpa) + numberOrZero(row.extraWork);
                return (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: DS.blue }}>
                      {month.label}
                    </h4>
                    <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
                      {[
                        ["scheduledWorks", "Scheduled Works"],
                        ["cpa",            "CPA"],
                        ["extraWork",      "Extra Work / VOs"],
                      ].map(([field, label]) => (
                        <div key={field}>
                          <label style={lbl}>{label}</label>
                          <input style={inp} type="number" value={row[field] ?? ""}
                            onChange={e => updateBlockValue("forecast", month.key, field, e.target.value)} />
                        </div>
                      ))}
                    </div>
                    <div style={{
                      background: DS.white, borderRadius: DS.r8,
                      padding: "10px 12px", border: `1px solid #BDD3F9`,
                      display: "flex", justifyContent: "space-between",
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: DS.blue }}>Forecast Total</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: DS.blue }}>{currency(total)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Actual panel */}
          {showActual && (
            <div style={{
              background: DS.tealSoft, border: `1px solid #9FDACB`,
              borderRadius: DS.r16, padding: "16px 18px",
            }}>
              <h4 style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: DS.teal }}>
                Actual Expenditure (from Certificates)
              </h4>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Actual Month</label>
                <select style={inp} value={selectedActualMonthKey}
                  onChange={e => setSelectedActualMonthKey(e.target.value)}>
                  {extendedMonthRange.map(m => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
              </div>

              {(() => {
                const month = extendedMonthRange.find(m => m.key === selectedActualMonthKey) || extendedMonthRange[0];
                if (!month) return null;
                const row  = activeActualBlocks[month.key] || {};
                const tots = getActualCertificateTotals(row);
                return (
                  <>
                    <h4 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: DS.teal }}>
                      {month.label}
                    </h4>
                    <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                      {[
                        ["Total Ex CPA",     currency(tots.actualTotalExCpa)],
                        ["CPA",              currency(tots.actualCpa)],
                        ["Extra Work / VOs", currency(tots.actualExtraWork)],
                      ].map(([label, val]) => (
                        <div key={label} style={{
                          display: "flex", justifyContent: "space-between",
                          padding: "8px 12px", background: DS.white,
                          borderRadius: DS.r8, border: `1px solid #9FDACB`,
                        }}>
                          <span style={{ fontSize: 12, color: DS.muted, fontWeight: 600 }}>{label}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: DS.teal }}>{val}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{
                      background: DS.white, borderRadius: DS.r8,
                      padding: "10px 12px", border: `1px solid #9FDACB`,
                      display: "flex", justifyContent: "space-between",
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: DS.teal }}>Actual Total</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: DS.teal }}>
                        {currency(tots.actualTotalIncCpa)}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </SectionCard>

      {/* ── FINANCIAL YEAR SECTIONS ──────────────── */}
      {Object.entries(grouped).map(([fy, rows]) => {
        const isExpanded = expandedFYears[fy] ?? true;

        return (
          <div key={fy} style={{
            background: DS.white, border: `1px solid ${DS.border}`,
            borderRadius: DS.r20, boxShadow: DS.shadowSm, marginBottom: 14, overflow: "hidden",
          }}>
            {/* FY card header */}
            <div style={{
              padding: "13px 20px", borderBottom: isExpanded ? `1px solid ${DS.border}` : "none",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: 10,
            }}>
              <div style={{ cursor: "pointer" }} onClick={() => toggleFY(fy)}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DS.text }}>
                  Financial Year {fy}
                </h3>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>
                  {rows.length} month{rows.length !== 1 ? "s" : ""} · click to {isExpanded ? "collapse" : "expand"}
                </p>
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Btn size="sm" variant="secondary" onClick={() => toggleFY(fy)}>
                  {isExpanded ? "▲ Hide" : "▼ Show"}
                </Btn>
                <Btn size="sm" variant="secondary" onClick={selectAllCols}>All Cols</Btn>
                <Btn size="sm" variant="secondary" onClick={showCoreCols}>Core Cols</Btn>
                <Btn size="sm" variant="secondary" onClick={resetColOrder}>Reset Order</Btn>
                <Btn size="sm" variant="secondary"
                  onClick={() => setShowColumnOptions(v => !v)}>
                  {showColumnOptions ? "Hide Col Options" : "Col Options"}
                </Btn>
                <Btn size="sm" variant="primary" onClick={() => exportFYPdf(fy, rows)}>
                  ⬇ Export PDF
                </Btn>
              </div>
            </div>

            {/* Column options picker */}
            {isExpanded && showColumnOptions && (
              <div style={{
                padding: "12px 20px", borderBottom: `1px solid ${DS.border}`,
                background: DS.surface,
                display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6,
              }}>
                {orderedColumns.map((col, idx) => (
                  <div key={col.key} style={{
                    display: "grid", gridTemplateColumns: "auto 1fr 72px", gap: 8,
                    alignItems: "center", fontSize: 11,
                  }}>
                    <input type="checkbox"
                      checked={col.alwaysVisible || Boolean(visibleCols[col.key])}
                      disabled={col.alwaysVisible}
                      onChange={() => toggleColumn(col.key)}
                      style={{ accentColor: DS.blue }} />
                    <span style={{ fontWeight: 500, color: DS.text }}>{col.label}</span>
                    <input type="number" min="1" max={orderedColumns.length}
                      value={idx + 1} disabled={col.key === "month"}
                      onChange={e => setColumnPosition(col.key, e.target.value)}
                      style={{ ...inp, fontSize: 11, padding: "3px 6px" }} />
                  </div>
                ))}
              </div>
            )}

            {isExpanded && (
              <div style={{ padding: "16px 20px" }}>
                {/* Bar chart */}
                <CashflowBarChart rows={rows} />

                {/* Detail table */}
                <div style={{ overflowX: "auto", marginTop: 20 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr>
                        {visibleColumns.map(col => (
                          <th key={col.key} style={{
                            padding: "7px 9px", textAlign: "left", whiteSpace: "nowrap",
                            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                            letterSpacing: "0.07em", color: DS.muted,
                            borderBottom: `2px solid ${DS.border}`, background: DS.surface,
                          }}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => (
                        <tr key={row.key} style={{ borderBottom: `1px solid ${DS.border}` }}
                          onMouseEnter={e => e.currentTarget.style.background = DS.surface}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          {visibleColumns.map(col => (
                            <td key={col.key} style={{
                              padding: "8px 9px",
                              ...(getCellStyle(row, col) || {}),
                            }}>
                              {getCellValue(row, col.key)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {/* Totals row */}
                      <tr style={{ borderTop: `2px solid ${DS.border}`, background: DS.surface, fontWeight: 700 }}>
                        {visibleColumns.map(col => {
                          if (col.key === "month") return <td key={col.key} style={{ padding: "8px 9px" }}>Total</td>;
                          const isCumulative = ["cumulativeForecastCpa","cumulativeActualCpa","cumulativeVariance"].includes(col.key);
                          const total = isCumulative
                            ? numberOrZero(rows[rows.length - 1]?.[col.key])
                            : rows.reduce((s, r) => s + numberOrZero(r[col.key]), 0);
                          return (
                            <td key={col.key} style={{
                              padding: "8px 9px",
                              ...(col.isVariance ? varianceStyle(total) : {}),
                            }}>
                              {currency(total)}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}